/**
 * useTipoCambio
 * ─────────────────────────────────────────────────────────────────────────────
 * Hook que obtiene el tipo de cambio CRC/USD del día desde el backend.
 * El backend consulta la API BCCR/Hacienda y lo persiste en BD.
 *
 * Returns:
 *   tipoCambio   → { fecha, tipo_compra, tipo_venta, fuente, es_hoy }
 *   loading      → boolean
 *   error        → string | null
 *   refetch      → () => void  (para forzar re-consulta)
 *   guardarTC    → (compra, venta) => Promise<void>  (override manual)
 */

import { useState, useEffect, useCallback } from "react";
import apiFetch from "../../../shared/services/apiClient";

export function useTipoCambio() {
  const [tipoCambio, setTipoCambio] = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);

  const fetchTC = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await apiFetch("/api/finance/tipo-cambio/hoy/");
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error al obtener TC");
      setTipoCambio(data);
    } catch (err) {
      setError(err.message || "No se pudo obtener el tipo de cambio");
      // Valor por defecto para no bloquear el formulario
      setTipoCambio({ tipo_compra: 1, tipo_venta: 1, fuente: "FALLBACK", es_hoy: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTC(); }, [fetchTC]);

  const guardarTC = useCallback(async (tipo_compra, tipo_venta) => {
    const res  = await apiFetch("/api/finance/tipo-cambio/hoy/", {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body:    JSON.stringify({ tipo_compra, tipo_venta }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error al guardar TC");
    setTipoCambio(data);
    return data;
  }, []);

  return { tipoCambio, loading, error, refetch: fetchTC, guardarTC };
}
